import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';

import { FacilitiesFormDialog } from './FacilitiesFormDialog';
import { FacilitiesRowActions } from './FacilitiesRowActions';
import { useFacilitiesList } from './useFacilities';
import type { Facility } from './types';

export const FacilitiesPage = () => {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const query = useFacilitiesList({ active_only: false });
  const rows = query.data ?? [];

  const columns = useMemo<MasterTableColumn<Facility>[]>(
    () => [
      {
        key: 'facility_name',
        header: t('efs.settings.facilities.fields.facility_name', { ns: 'settings' }),
        cell: (row) => row.facility_name,
      },
      {
        key: 'company_display_name',
        header: t('efs.settings.facilities.fields.company', { ns: 'settings' }),
        cell: (row) => row.company_display_name,
      },
      {
        key: 'company_party_type',
        header: t('efs.settings.facilities.fields.company_type', { ns: 'settings' }),
        cell: (row) =>
          t(`efs.settings.companies.types.${row.company_party_type}`, {
            ns: 'settings',
            defaultValue: row.company_party_type,
          }),
      },
      {
        key: 'facility_role',
        header: t('efs.settings.facilities.fields.facility_role', { ns: 'settings' }),
        cell: (row) => t(`efs.settings.facilities.roles.${row.facility_role}`, { ns: 'settings' }),
      },
      {
        key: 'address',
        header: t('efs.settings.facilities.fields.address', { ns: 'settings' }),
        cell: (row) => row.address ?? '—',
      },
      {
        key: 'active',
        header: t('efs.settings.facilities.fields.active', { ns: 'settings' }),
        cell: (row) =>
          row.active_flag ? (
            <Badge variant="default">
              {t('efs.common.status.approved', { defaultValue: '有効' })}
            </Badge>
          ) : (
            <Badge variant="outline">
              {t('efs.common.status.retired', { defaultValue: '無効' })}
            </Badge>
          ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => <FacilitiesRowActions row={row} />,
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.settings.facilities.title', { ns: 'settings' })}
        subtitle={t('efs.settings.facilities.subtitle', { ns: 'settings' })}
        count={rows.length}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('efs.settings.facilities.actions.create', { ns: 'settings' })}
          </Button>
        }
      />

      <TableToolbar count={t('efs.masters.toolbar.count', { ns: 'masters', count: rows.length })} />

      <MasterTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.facility_id}
        isLoading={query.isLoading}
      />

      <FacilitiesFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
};

export default FacilitiesPage;
